import { useEffect, useReducer } from 'react';
import ElapsedTime from './ElapsedTime';
import styles from './WorkingIndicator.module.css';

// Shown while a hunt / verification is running.
//
// This used to be one static sentence that sat there for however long the search took. At
// twenty seconds that reads as reassurance; at two and a half minutes it reads as a page that
// has stopped updating, which is the exact impression it exists to prevent. So it now says
// different things as the wait grows, and carries a clock.
//
// The copy escalates rather than repeating, because the useful message changes with time. Early
// on the user wants to know roughly what is happening. Later they want to know that the slowness
// is the system being careful rather than the system being stuck. Past two minutes they mostly
// want to know it is still running at all, and why a search like theirs takes this long.
//
// Nothing here promises they can cancel: the Stop button currently hides a running search
// rather than stopping it, and the credits are still spent. Do not add that claim until Stop
// aborts the request for real.
const BUCKETS = [
  {
    after: 0,
    text: "This can take a moment because we're searching live sources and verifying in real "
        + 'time, not just pulling from a stale database. Hang tight.',
  },
  {
    after: 20_000,
    text: 'Still going. We search live sources and verify in real time, which is slower than '
        + "reading a stale database - it's also why the addresses work.",
  },
  {
    after: 60_000,
    text: 'This one is taking a while. Finding companies that match your ICP is the slow step, '
        + 'and we check each one rather than guessing. Nothing is stuck.',
  },
  {
    after: 150_000,
    text: 'Still working. Long searches usually mean a narrow ICP - a tight industry, a small '
        + 'state list, or a strict revenue range. The results will land here when they are ready.',
  },
];

function copyFor(elapsedMs) {
  let chosen = BUCKETS[0].text;
  for (const b of BUCKETS) if (elapsedMs >= b.after) chosen = b.text;
  return chosen;
}

export default function WorkingIndicator({ since }) {
  // Re-render on the same cadence as the clock so the copy swaps buckets on time. Cheap: this
  // component is three spans, and it only exists while a search is running.
  const [, tick] = useReducer(n => n + 1, 0);
  useEffect(() => {
    if (!since) return undefined;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [since]);

  const elapsed = since ? Date.now() - since : 0;

  return (
    <div className={styles.wrap}>
      <span className={styles.spinner} aria-hidden="true" />
      {/* The live region is the copy alone. Putting the ticking clock inside it would make a
          screen reader read the elapsed time out loud once a second, forever. */}
      <span className={styles.text} role="status" aria-live="polite">{copyFor(elapsed)}</span>
      <ElapsedTime since={since} />
    </div>
  );
}
